import { useEffect, useState } from "react";

export function readStorage<T>(key: string, fallback: T, normalize: (value: unknown) => T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return normalize(JSON.parse(raw));
  } catch {
    return fallback;
  }
}

export function writeStorage<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write failures so user preferences remain best-effort.
  }
}

export function numberInRange(fallback: number, min: number, max: number) {
  return (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
  };
}

export function stringValue(fallback: string) {
  return (value: unknown): string => (typeof value === "string" && value ? value : fallback);
}

export function booleanValue(fallback: boolean) {
  return (value: unknown): boolean => (typeof value === "boolean" ? value : fallback);
}

export function useStoredState<T>(
  key: string,
  fallback: T,
  normalize: (value: unknown) => T,
): [T, (value: T) => void] {
  const [state, setState] = useState(() => readStorage(key, fallback, normalize));
  useEffect(() => {
    writeStorage(key, state);
  }, [key, state]);
  return [state, setState];
}
